import React from "react";
import { InlineMath } from "react-katex";
import FractionInput, {
  formatLatexPreview,
} from "../../../../components/inputs/FractionInput";

function randInt(min, max) {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

function formatMath2(v) {
  return Number(v).toFixed(2);
}

function placeName(colFromRight) {
  if (colFromRight === 0) return "setnych";
  if (colFromRight === 1) return "dziesiętnych";
  if (colFromRight === 2) return "jedności";
  if (colFromRight === 3) return "dziesiątek";
  if (colFromRight === 4) return "setek";
  return `${colFromRight - 1}. rzędu`;
}

function pickData() {
  for (let i = 0; i < 1200; i += 1) {
    // 1/3: od liczby całkowitej, 2/3: od liczby z 1 miejscem po przecinku.
    const integerMinuend = Math.random() < 1 / 3;

    const aUnits = integerMinuend
      ? randInt(20, 99) * 100 // x,00
      : randInt(200, 999) * 10; // x,x0
    const bUnits = randInt(1000, 8999); // x,xx

    if (bUnits % 10 === 0) continue; // Żeby nie było końcowego zera
    if (!integerMinuend && Math.floor(aUnits / 10) % 10 === 0) continue; // bez x.0
    if (aUnits <= bUnits) continue; // wynik dodatni

    const intCols = Math.max(
      String(Math.floor(aUnits / 100)).length,
      String(Math.floor(bUnits / 100)).length
    );
    const cols = intCols + 2;

    const aDigits = String(aUnits)
      .padStart(cols, "0")
      .split("")
      .map((d) => Number(d));
    const bDigits = String(bUnits)
      .padStart(cols, "0")
      .split("")
      .map((d) => Number(d));

    const cur = [...aDigits];
    let hasBorrow = false;
    for (let c = cols - 1; c >= 0; c -= 1) {
      if (cur[c] < bDigits[c]) {
        hasBorrow = true;
        break;
      }
    }
    if (!hasBorrow) continue;

    const aDisplay = integerMinuend
      ? String(aUnits / 100)
      : formatMath2(aUnits / 100).replace(/0$/, "");
    const bDisplay = formatMath2(bUnits / 100);

    return {
      aUnits,
      bUnits,
      resultUnits: aUnits - bUnits,
      a: aUnits / 100,
      b: bUnits / 100,
      result: (aUnits - bUnits) / 100,
      aDisplay,
      bDisplay,
    };
  }

  return {
    aUnits: 6330,
    bUnits: 468,
    resultUnits: 5862,
    a: 63.3,
    b: 4.68,
    result: 58.62,
    aDisplay: "63.3",
    bDisplay: "4.68",
  };
}

function analyzeSubtraction(aUnits, bUnits) {
  const intCols = Math.max(
    String(Math.floor(aUnits / 100)).length,
    String(Math.floor(bUnits / 100)).length
  );
  const cols = intCols + 2;

  const aDigits = String(aUnits)
    .padStart(cols, "0")
    .split("")
    .map((d) => Number(d));
  const bDigits = String(bUnits)
    .padStart(cols, "0")
    .split("")
    .map((d) => Number(d));

  const current = [...aDigits];
  const resultDigits = Array(cols).fill("");
  const detailLines = [];
  const steps = [];

  const baseStruck = new Set();
  const active = Array(cols).fill(""); // ostatnia wartość nad kolumną
  const replaced = Array(cols).fill(""); // poprzednia (gdy nadpisana)

  function applyModification(idx, nextVal, state) {
    const { changedIndices, replacedCurrent, baseCurrent } = state;

    if (active[idx] !== "") {
      replaced[idx] = String(active[idx]);
      replacedCurrent.push(idx);
    } else {
      baseStruck.add(idx);
      baseCurrent.push(idx);
    }

    active[idx] = String(nextVal);
    changedIndices.push(idx);
  }

  function borrowAt(col, state) {
    let donor = col - 1;
    while (donor >= 0 && current[donor] === 0) donor -= 1;
    if (donor < 0) return;

    current[donor] -= 1;
    applyModification(donor, current[donor], state);

    for (let k = donor + 1; k < col; k += 1) {
      current[k] = 9;
      applyModification(k, current[k], state);
    }

    current[col] += 10;
    applyModification(col, current[col], state);
  }

  for (let c = cols - 1; c >= 0; c -= 1) {
    const colFromRight = cols - 1 - c;
    const before = current[c];
    const sub = bDigits[c];

    const state = {
      changedIndices: [],
      replacedCurrent: [],
      baseCurrent: [],
    };

    if (before < sub) {
      borrowAt(c, state);
    }

    const minuendAtCol = current[c];
    const diff = minuendAtCol - sub;
    resultDigits[c] = String(diff);

    detailLines.push({
      place: placeName(colFromRight),
      before,
      sub,
      minuend: minuendAtCol,
      diff,
      borrowed: before < sub,
    });

    steps.push({
      strikeDigits: replaced.map((v) => (v ? String(v) : "")),
      newDigits: active.map((v) => (v ? String(v) : "")),
      changedIndices: state.changedIndices,
      replacedCurrent: state.replacedCurrent,
      baseStruck: Array.from(baseStruck),
      baseCurrent: state.baseCurrent,
      aDigits: aDigits.map(String),
      bDigits: bDigits.map(String),
      resultDigits: [...resultDigits],
      highlightResult: [c],
      showComma: false,
      intCols,
    });
  }

  steps.push({
    strikeDigits: replaced.map((v) => (v ? String(v) : "")),
    newDigits: active.map((v) => (v ? String(v) : "")),
    changedIndices: [],
    replacedCurrent: [],
    baseStruck: Array.from(baseStruck),
    baseCurrent: [],
    aDigits: aDigits.map(String),
    bDigits: bDigits.map(String),
    resultDigits: [...resultDigits],
    highlightResult: [],
    showComma: true,
    intCols,
  });

  return { detailLines, steps };
}

const ORANGE = "#f59e0b";

function Row({
  sign,
  digits,
  showComma,
  intCols,
  reserveComma = false,
  textHighlightIndices = [],
  strikeIndices = [],
  strikeCurrentIndices = [],
  small = false,
}) {
  const commaAfter = intCols - 1;
  const merged = [];

  for (let i = 0; i < digits.length; i += 1) {
    merged.push({ type: "digit", v: digits[i], idx: i });
    if (reserveComma && i === commaAfter) merged.push({ type: "comma", visible: showComma });
  }

  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: `1ch repeat(${merged.length}, 1ch)`,
        columnGap: "0.08em",
        alignItems: "end",
        whiteSpace: "nowrap",
      }}
    >
      <span>{sign || "\u00A0"}</span>
      {merged.map((x, i) => {
        if (x.type === "comma") {
          return (
            <span key={`c-${i}`} style={x.visible ? undefined : { opacity: 0 }}>
              ,
            </span>
          );
        }

        const strike = strikeIndices.includes(x.idx);
        const strikeCurrent = strikeCurrentIndices.includes(x.idx);
        const textHl = textHighlightIndices.includes(x.idx);

        const style = {
          fontSize: small ? "0.72em" : "1em",
          color: textHl ? ORANGE : "var(--color-text)",
          ...(small && String(x.v).length > 1
            ? {
                fontSize: "0.62em",
                letterSpacing: "-0.12em",
              }
            : null),
          ...(strike
            ? {
                textDecorationLine: "line-through",
                textDecorationColor: strikeCurrent ? ORANGE : "var(--color-text)",
                textDecorationThickness: "2px",
                textDecorationStyle: "solid",
              }
            : null),
        };

        return (
          <span key={`d-${x.idx}`} style={style}>
            {x.v === "" ? "\u00A0" : x.v}
          </span>
        );
      })}
    </div>
  );
}

function MiniBoard({ step }) {
  const hasReplaced = step.strikeDigits.map((v) => v !== "");

  // Wiersz bliżej liczby: pierwsza modyfikacja kolumny.
  const lowerDigits = step.strikeDigits.map((v, i) => (hasReplaced[i] ? v : step.newDigits[i]));
  // Wiersz wyżej: druga modyfikacja (jeśli była).
  const upperDigits = step.newDigits.map((v, i) => (hasReplaced[i] ? v : ""));

  const lowerTextCurrent = step.changedIndices.filter((i) => !hasReplaced[i]);
  const upperTextCurrent = step.changedIndices.filter((i) => hasReplaced[i]);

  const lowerStrikeIndices = hasReplaced
    .map((ok, i) => (ok ? i : -1))
    .filter((i) => i >= 0);
  const hasUpperRow = upperDigits.some((v) => v !== "");

  return (
    <div
      style={{
        padding: "10px 14px",
        border: "1px solid var(--color-border)",
        borderRadius: 8,
        background: "transparent",
        width: "fit-content",
      }}
    >
      <div
        style={{
          fontFamily: "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace",
          lineHeight: 1.2,
          display: "inline-block",
        }}
      >
        {/* wyżej: kolejna modyfikacja */}
        {hasUpperRow ? (
          <Row
            sign=""
            digits={upperDigits}
            showComma={false}
            intCols={step.intCols}
            reserveComma
            small
            textHighlightIndices={upperTextCurrent}
          />
        ) : null}

        {/* bliżej liczby: pierwsza modyfikacja */}
        <Row
          sign=""
          digits={lowerDigits}
          showComma={false}
          intCols={step.intCols}
          reserveComma
          small
          textHighlightIndices={lowerTextCurrent}
          strikeIndices={lowerStrikeIndices}
          strikeCurrentIndices={step.replacedCurrent}
        />

        <Row
          sign=""
          digits={step.aDigits}
          showComma
          intCols={step.intCols}
          reserveComma
          strikeIndices={step.baseStruck}
          strikeCurrentIndices={step.baseCurrent}
        />

        <Row sign="-" digits={step.bDigits} showComma intCols={step.intCols} reserveComma />
        <div style={{ margin: "2px 0", borderTop: "1px solid var(--color-text)", width: "100%" }} />

        <Row
          sign=""
          digits={step.resultDigits}
          showComma={step.showComma}
          intCols={step.intCols}
          reserveComma
          textHighlightIndices={step.highlightResult}
        />
      </div>
    </div>
  );
}

export function generateTask() {
  return {
    type: "DecimalSubtractionVariableTask",
    data: pickData(),
  };
}

export function renderTask({ data }) {
  return (
    <div style={{ textAlign: "left" }}>
      <p>Oblicz: <InlineMath math={`${data.aDisplay} - ${data.bDisplay}`} /></p>
    </div>
  );
}

export function renderInput(_task, userAnswer, onChange, disabled) {
  return (
    <FractionInput
      value={userAnswer}
      onChange={onChange}
      disabled={disabled}
      label="wynik"
      placeholder="np. 12,35 lub 12.35"
    />
  );
}

export function checkAnswer(task, userAnswer) {
  if (!userAnswer || typeof userAnswer !== "object") return false;
  const parsed = userAnswer.parsed;
  if (parsed == null || !Number.isFinite(parsed)) return false;
  return Math.abs(parsed - task.data.result) < 1e-9;
}

export function renderAnswer({ data }) {
  return <InlineMath math={formatMath2(data.result).replace(".", "{,}")} />;
}

export function renderUserAnswer(userAnswer) {
  if (!userAnswer || typeof userAnswer !== "object" || !userAnswer.raw) {
    return <i>(brak odpowiedzi)</i>;
  }
  return <InlineMath math={formatLatexPreview(userAnswer.raw)} />;
}

export function renderExplanation({ data }) {
  const written = analyzeSubtraction(data.aUnits, data.bUnits);

  return (
    <div style={{ textAlign: "left" }}>
      <p>
        <strong>Krok 1:</strong> Ustawiamy liczby pionowo (przecinek pod przecinkiem).
      </p>
      <p>Dopisujemy brakujące zera na końcu odjemnej, aby wyrównać miejsca po przecinku.</p>

      <p>
        <strong>Krok 2:</strong> Odejmujemy od prawej strony. Gdy w kolumnie nie da się odjąć, pożyczamy 1 z lewej
        kolumny (czyli +10 w bieżącej kolumnie).
      </p>

      <ul style={{ marginTop: 6, marginBottom: 8 }}>
        {written.detailLines.map((line, idx) => (
          <li key={idx}>
            {line.place}:{" "}
            {line.borrowed ? (
              <>
                <InlineMath math={`${line.before}<${line.sub}`} /> więc pożyczamy <InlineMath math={"1"} /> z lewej
                kolumny i liczymy <InlineMath math={`${line.minuend}-${line.sub}=${line.diff}`} />
              </>
            ) : (
              <InlineMath math={`${line.before}-${line.sub}=${line.diff}`} />
            )}
          </li>
        ))}
        <li>na końcu dopisujemy przecinek w wyniku dokładnie pod przecinkiem składników</li>
      </ul>

      <div
        style={{
          marginTop: 8,
          marginBottom: 10,
          padding: 10,
          border: "1px solid var(--color-border)",
          borderRadius: 8,
          background: "transparent",
        }}
      >
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            gap: 8,
          }}
        >
          {written.steps.map((step, idx) => (
            <React.Fragment key={idx}>
              <MiniBoard step={step} />
              {idx < written.steps.length - 1 ? (
                <div style={{ fontSize: "1.2rem", fontWeight: 700, color: "var(--color-text)" }}>{"\u2192"}</div>
              ) : null}
            </React.Fragment>
          ))}
        </div>
      </div>

      <p>
        <strong>Wynik:</strong> <InlineMath math={formatMath2(data.result).replace(".", "{,}")} />
      </p>
    </div>
  );
}

